import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Button, Paper, Alert, CircularProgress } from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import SendIcon from "@mui/icons-material/Send";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { colors, primaryGradient } from "../../theme/colors";

export default function EmailOTPVerification({
  maskedEmail,
  candidateName,
  onSendOTP,
  onVerifyOTP,
  isLoading,
  errorMsg,
  isSendingOTP = false,
  isVerifyingOTP = false,
}) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const inputRefs = useRef([]);

  const sending = isSending || isSendingOTP;
  const verifying = isLoading || isVerifyingOTP;

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendOTP = async () => {
    if (sending) return;
    setIsSending(true);
    try {
      const success = await onSendOTP();
      if (success !== false) {
        setOtpSent(true);
        setResendTimer(60);
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasteData)) {
      const digits = pasteData.split("");
      setOtp(digits);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullOtp = otp.join("");
    if (fullOtp.length === 6) {
      onVerifyOTP(fullOtp);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${colors.gradient.g8 || "#1a0404"} 0%, ${colors.gradient.g6 || "#450a0a"} 50%, ${colors.gradient.g3 || "#7f1d1d"} 100%)`,
        padding: 3,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          maxWidth: 480,
          width: "100%",
          padding: { xs: 3, sm: 5 },
          borderRadius: 4,
          textAlign: "center",
          backgroundColor: "#ffffff",
          color: colors.text.primary,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
          border: `1px solid ${colors.primaryLightBorder}`,
        }}
      >
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: "50%",
            backgroundColor: otpSent ? "#ecfdf5" : colors.primaryLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px auto",
            border: `2px solid ${otpSent ? "#a7f3d0" : colors.primaryLightBorder}`,
          }}
        >
          {otpSent ? (
            <MarkEmailReadOutlinedIcon sx={{ fontSize: 38, color: "#059669" }} />
          ) : (
            <LockResetIcon sx={{ fontSize: 38, color: colors.primary }} />
          )}
        </Box>

        <Typography
          variant="h5"
          fontWeight="700"
          gutterBottom
          sx={{ color: colors.text.primary, letterSpacing: "-0.5px" }}
        >
          Candidate Identity Verification
        </Typography>

        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3, lineHeight: 1.6 }}>
          {candidateName ? `Hello ${candidateName}, ` : ""}an interview has been scheduled for your registered email address.
        </Typography>

        {errorMsg && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              textAlign: "left",
              borderRadius: 2,
              backgroundColor: "#fef2f2",
              color: colors.primaryHover,
              border: `1px solid ${colors.primaryLightBorder}`,
            }}
          >
            {errorMsg}
          </Alert>
        )}

        {!otpSent ? (
          <Box sx={{ my: 3 }}>
            {maskedEmail && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: colors.primaryLight,
                  border: `1px solid ${colors.primaryLightBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <EmailOutlinedIcon sx={{ color: colors.primary, fontSize: 20 }} />
                <Typography variant="body1" fontWeight="600" sx={{ color: colors.primary, letterSpacing: 0.5 }}>
                  {maskedEmail}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSendOTP}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              sx={{
                py: 1.6,
                borderRadius: 3,
                fontWeight: "600",
                fontSize: "1rem",
                textTransform: "none",
                backgroundColor: colors.primary,
                color: colors.white,
                boxShadow: "0 4px 14px rgba(127, 29, 29, 0.35)",
                transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s ease",
                "&:hover": {
                  backgroundColor: colors.primaryHover,
                  boxShadow: "0 6px 20px rgba(69, 10, 10, 0.45)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                "&:disabled": {
                  backgroundColor: `${colors.buttonDisabled.background} !important`,
                  color: `${colors.buttonDisabled.color} !important`,
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {sending ? "Sending OTP..." : "Send Verification OTP to Email"}
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
              Enter the 6-digit verification code sent to <strong style={{ color: colors.primary }}>{maskedEmail || "your email"}</strong>:
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, my: 3 }} onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  style={{
                    width: 48,
                    height: 56,
                    fontSize: 24,
                    fontWeight: "bold",
                    textAlign: "center",
                    borderRadius: 8,
                    border: digit ? `2px solid ${colors.primary}` : `1px solid ${colors.border.default}`,
                    backgroundColor: digit ? colors.primaryLight : "#ffffff",
                    color: colors.text.primary,
                    outline: "none",
                    transition: "all 0.2s ease-in-out",
                  }}
                />
              ))}
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={verifying || otp.join("").length !== 6}
              sx={{
                py: 1.6,
                borderRadius: 3,
                fontWeight: "600",
                fontSize: "1rem",
                textTransform: "none",
                backgroundColor: colors.primary,
                color: colors.white,
                boxShadow: "0 4px 14px rgba(127, 29, 29, 0.35)",
                transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s ease",
                "&:hover": {
                  backgroundColor: colors.primaryHover,
                  boxShadow: "0 6px 20px rgba(69, 10, 10, 0.45)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                "&:disabled": {
                  backgroundColor: `${colors.buttonDisabled.background} !important`,
                  color: `${colors.buttonDisabled.color} !important`,
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {verifying ? <CircularProgress size={24} color="inherit" /> : "Verify & Start Interview"}
            </Button>

            <Box sx={{ mt: 3 }}>
              {resendTimer > 0 ? (
                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                  Resend code in <strong>{resendTimer}s</strong>
                </Typography>
              ) : (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSendOTP}
                  disabled={verifying || sending || resendTimer > 0}
                  startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{
                    color: colors.link,
                    textTransform: "none",
                    fontWeight: "600",
                    "&:hover": {
                      textDecoration: "underline",
                      backgroundColor: "transparent",
                    },
                    "&:disabled": {
                      color: colors.buttonDisabled.color,
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {sending ? "Sending code..." : "Didn't receive code? Resend OTP"}
                </Button>
              )}
            </Box>
          </form>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8, mt: 4 }}>
          <LockOutlinedIcon sx={{ fontSize: 16, color: colors.text.muted }} />
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            Secure 6-Digit Email Verification System
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
